import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Card,
    Avatar,
    Typography,
    Chip,
    styled,
    IconButton,
    Tooltip,
    useTheme,
    useMediaQuery,
    Button,
} from "@mui/material";

export interface HoverAction {
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
}

export interface MainButton {
    text: string;
    onClick: () => void;
}

export interface EntityCardProps {
    title: string;
    subtitle?: string;
    avatar?: {
        initials?: string;
        backgroundColor?: string;
    };
    badge?: {
        label: string;
        color?: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning";
        variant?: "filled" | "outlined";
    };
    hoverActions?: HoverAction[];
    mainButton?: MainButton;
    isHighlighted?: boolean;
    onCardClick?: () => void;
}

const EntityCardContainer = styled(Card) <{ isHighlighted?: boolean }>`
    align-items: center;
    background-color: ${({ theme, isHighlighted }) => isHighlighted ? theme.palette.action.selected : 'inherit'};
    border: ${({ theme, isHighlighted }) => isHighlighted ? `2px solid ${theme.palette.primary.main}` : 'none'};
    border-radius: ${({ theme }) => theme.spacing(2)};
    display: flex;
    flex-direction: row;
    gap: ${({ theme }) => theme.spacing(2)};
    min-height: 72px;
    min-width: 200px;
    padding: ${({ theme }) => theme.spacing(2)};
    position: relative;
    transition: all 0.3s ease;

    &:hover {
        box-shadow: ${({ theme }) => theme.shadows[8]};
    }
`;

const HoverOverlay = styled(Box) <{ showOverlay?: boolean }>`
    align-items: center;
    backdrop-filter: blur(4px);
    background-color: ${({ theme }) => theme.palette.action.hover};
    bottom: 0;
    border-radius: ${({ theme }) => theme.spacing(2)};
    display: flex;
    gap: ${({ theme }) => theme.spacing(1)};
    justify-content: center;
    left: 0;
    opacity: ${({ showOverlay }) => showOverlay ? 1 : 0};
    pointer-events: ${({ showOverlay }) => showOverlay ? 'auto' : 'none'};
    position: absolute;
    right: 0;
    top: 0;
    transition: opacity 0.2s ease;
    z-index: 1;
`;

const EntityAvatar = styled(Avatar)`
    color: ${({ theme }) => theme.palette.common.white};
    flex-shrink: 0;
    height: 56px;
    font-size: ${({ theme }) => theme.typography.body1.fontSize};
    font-weight: ${({ theme }) => theme.typography.fontWeightBold};
    width: 56px;
`;

const EntityInfo = styled(Box)`
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(0.5)};
    min-width: 0;
`;

const ActionButton = styled(Button)`
    flex-shrink: 0;
    font-weight: ${({ theme }) => theme.typography.fontWeightMedium};
`;

const BadgeContainer = styled(Box)`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
    min-width: 0;
    overflow: hidden;
`;

function EntityCard({
    title,
    subtitle,
    avatar,
    badge,
    hoverActions = [],
    mainButton,
    isHighlighted = false,
    onCardClick,
}: EntityCardProps) {
    const [showOverlay, setShowOverlay] = useState(false);
    const cardRef = useRef<HTMLDivElement>(null);

    const theme = useTheme();
    const isTabletOrMobile = useMediaQuery(theme.breakpoints.down("md"));

    const displayInitials =
        avatar?.initials || title.split(" ").map((n) => n[0]).join("").toUpperCase();
    const hasActions = hoverActions.length > 0;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                isTabletOrMobile &&
                showOverlay &&
                cardRef.current &&
                !cardRef.current.contains(event.target as Node)
            ) {
                setShowOverlay(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isTabletOrMobile, showOverlay]);

    const handleCardClick = (e: React.MouseEvent) => {
        if (isTabletOrMobile && hasActions) {
            const target = e.target as HTMLElement;
            if (!target.closest("button") && !target.closest(".MuiChip-root")) {
                setShowOverlay(!showOverlay);
            }
        }
        onCardClick?.();
    };

    const handleActionClick = (callback: () => void) => {
        return (e: React.MouseEvent) => {
            e.stopPropagation();
            setShowOverlay(false);
            callback();
        };
    };

    return (
        <EntityCardContainer
            ref={cardRef}
            isHighlighted={isHighlighted}
            onMouseEnter={() => !isTabletOrMobile && hasActions && setShowOverlay(true)}
            onMouseLeave={() => !isTabletOrMobile && setShowOverlay(false)}
            onClick={handleCardClick}
        >
            {avatar && (
                <EntityAvatar sx={{ backgroundColor: avatar.backgroundColor || "#FF9800" }}>
                    {displayInitials}
                </EntityAvatar>
            )}

            <EntityInfo>
                <Typography variant="body1" sx={{ fontWeight: theme.typography.fontWeightBold }} noWrap>
                    {title}
                </Typography>
                {subtitle && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                        {subtitle}
                    </Typography>
                )}
            </EntityInfo>

            <BadgeContainer>
                {badge && (
                    <Chip
                        label={badge.label}
                        color={badge.color || "primary"}
                        variant={badge.variant || "outlined"}
                        sx={{ fontWeight: theme.typography.fontWeightMedium, userSelect: "none" }}
                    />
                )}
                {mainButton && (
                    <ActionButton
                        variant="outlined"
                        color="primary"
                        onClick={handleActionClick(mainButton.onClick)}
                    >
                        {mainButton.text}
                    </ActionButton>
                )}
            </BadgeContainer>

            {hasActions && (
                <HoverOverlay showOverlay={showOverlay}>
                    {hoverActions.map((action, index) => (
                        <Tooltip key={index} title={action.label}>
                            <IconButton
                                size="medium"
                                onClick={handleActionClick(action.onClick)}
                                aria-label={action.label}
                            >
                                {action.icon}
                            </IconButton>
                        </Tooltip>
                    ))}
                </HoverOverlay>
            )}
        </EntityCardContainer>
    );
}

export default EntityCard;
